import React, { useState, useEffect } from "react";
import style from "./style.module.scss";
import Modal from "../../../components/modal";
import Button from "../../../components/new-button";
import TextInput from "../../../components/form/text-input";
import { get2faStatus, setup2fa, manualKey2fa, enable2fa, disable2fa } from "../../../../api";
import { toast } from "react-hot-toast";

function TwoFactorAuth({ isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [step, setStep] = useState("status"); // status, confirm-password, setup, recovery, disable
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [setupPassword, setSetupPassword] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [revealingKey, setRevealingKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
    } else {
      setStep("status");
      setQrCode(null);
      setSecret(null);
      setVerifyCode("");
      setRecoveryCodes([]);
      setSetupPassword("");
      setDisablePassword("");
      setRevealingKey(false);
      setShowSecret(false);
    }
  }, [isOpen]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await get2faStatus();
      if (res.success) {
        setEnabled(res.enabled);
      }
    } catch (error) {
      toast.error("Failed to fetch 2FA status");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    if (!setupPassword) {
      toast.error("Please enter your password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await setup2fa({ password: setupPassword });
      if (res.success) {
        setQrCode(res.qr_code);
        setSecret(null);
        setStep("setup");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Failed to set up 2FA");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevealKey = async () => {
    if (!setupPassword) {
      toast.error("Please enter your password");
      return;
    }
    setRevealingKey(true);
    try {
      const res = await manualKey2fa({ password: setupPassword });
      if (res.success) {
        setSecret(res.secret);
        setShowSecret(true);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Failed to reveal setup key");
    } finally {
      setRevealingKey(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      const res = await enable2fa({ code: verifyCode });
      if (res.success) {
        setRecoveryCodes(res.recovery_codes);
        setEnabled(true);
        setStep("recovery");
        toast.success("Two-factor authentication enabled");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Invalid verification code");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      toast.error("Please enter your password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await disable2fa({ password: disablePassword });
      if (res.success) {
        setEnabled(false);
        setStep("status");
        setDisablePassword("");
        toast.success("Two-factor authentication disabled");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Failed to disable 2FA");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatus = () => (
    <>
      <div className={style.body}>
        <div className={style.status_container}>
          <span className={style.status_text}>Two-Factor Authentication</span>
          <span className={`${style.status_badge} ${enabled ? style.status_enabled : style.status_disabled}`}>
            {enabled ? "Enabled" : "Disabled"}
          </span>
        </div>
        <p className={style.info_text}>
          {enabled
            ? "Your account is protected with two-factor authentication. You will need to enter a code from your authenticator app when signing in."
            : "Add an extra layer of security to your account by enabling two-factor authentication. You will need an authenticator app like Google Authenticator or Authy."}
        </p>
      </div>
      <div className={style.footer}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
        {enabled ? (
          <Button type="button" variant="secondary" onClick={() => setStep("disable")}>
            Disable 2FA
          </Button>
        ) : (
          <Button type="button" variant="primary" onClick={() => setStep("confirm-password")}>
            Set Up 2FA
          </Button>
        )}
      </div>
    </>
  );

  const renderConfirmPassword = () => (
    <>
      <div className={style.body}>
        <p className={style.info_text}>
          Enter your password to continue setting up two-factor authentication.
        </p>
        <TextInput
          label="Password"
          name="setup_password"
          type="password"
          placeholder="Enter your password"
          value={setupPassword}
          onChange={(e) => setSetupPassword(e.target.value)}
        />
      </div>
      <div className={style.footer}>
        <Button type="button" variant="ghost" onClick={() => { setStep("status"); setSetupPassword(""); }}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleSetup} disabled={submitting || !setupPassword} isLoading={submitting}>
          {submitting ? "Verifying..." : "Continue"}
        </Button>
      </div>
    </>
  );

  const renderSetup = () => (
    <>
      <div className={style.body}>
        <p className={style.step_label}>Step 1: Scan the QR code with your authenticator app</p>
        <div className={style.qr_container}>
          {qrCode && (
            <img
              className={style.qr_code}
              src={`data:image/svg+xml;base64,${qrCode}`}
              alt="QR Code"
            />
          )}
          <span className={style.secret_label}>Can’t scan?</span>
          <Button
            type="button"
            variant="ghost"
            onClick={handleRevealKey}
            disabled={revealingKey}
            isLoading={revealingKey}
          >
            <span>{revealingKey ? "Revealing..." : "Reveal setup key"}</span>
          </Button>

          {secret && (
            <>
              <span className={style.secret_label}>Setup key (keep this private):</span>
              <span className={style.secret_key}>{showSecret ? secret : "••••••••••••••••"}</span>
              <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "8px" }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowSecret((s) => !s)}
                >
                  <span>{showSecret ? "Hide" : "Show"}</span>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    toast.success("Setup key copied to clipboard");
                  }}
                >
                  <span>Copy</span>
                </Button>
              </div>
            </>
          )}
        </div>
        <p className={style.step_label}>Step 2: Enter the 6-digit code from your app</p>
        <TextInput
          label=""
          name="verify_code"
          placeholder="Enter 6-digit code"
          value={verifyCode}
          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          maxLength={6}
        />
      </div>
      <div className={style.footer}>
        <Button type="button" variant="ghost" onClick={() => setStep("status")}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleVerify} disabled={submitting || verifyCode.length !== 6} isLoading={submitting}>
          {submitting ? "Verifying..." : "Verify & Enable"}
        </Button>
      </div>
    </>
  );

  const renderRecovery = () => (
    <>
      <div className={style.body}>
        <p className={style.step_label}>Save your recovery codes</p>
        <p className={style.recovery_warning}>
          Store these codes in a safe place. Each code can only be used once. If you lose access to your authenticator app, you can use these codes to sign in.
        </p>
        <div className={style.recovery_codes}>
          {recoveryCodes.map((code, index) => (
            <span key={index} className={style.recovery_code}>{code}</span>
          ))}
        </div>
      </div>
      <div className={style.footer}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            navigator.clipboard.writeText(recoveryCodes.join("\n"));
            toast.success("Recovery codes copied to clipboard");
          }}
        >
          Copy Codes
        </Button>
        <Button type="button" variant="primary" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );

  const renderDisable = () => (
    <>
      <div className={style.body}>
        <p className={style.info_text}>
          Enter your password to confirm disabling two-factor authentication.
        </p>
        <TextInput
          label="Password"
          name="disable_password"
          type="password"
          placeholder="Enter your password"
          value={disablePassword}
          onChange={(e) => setDisablePassword(e.target.value)}
        />
      </div>
      <div className={style.footer}>
        <Button type="button" variant="ghost" onClick={() => { setStep("status"); setDisablePassword(""); }}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleDisable} disabled={submitting || !disablePassword} isLoading={submitting}>
          {submitting ? "Disabling..." : "Disable 2FA"}
        </Button>
      </div>
    </>
  );

  const getHeader = () => {
    switch (step) {
      case "confirm-password": return "Confirm Password";
      case "setup": return "Set Up Two-Factor Authentication";
      case "recovery": return "Recovery Codes";
      case "disable": return "Disable Two-Factor Authentication";
      default: return "Two-Factor Authentication";
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} header={getHeader()} size="sm">
      {loading ? (
        <div className={style.body}>
          <p className={style.info_text}>Loading...</p>
        </div>
      ) : (
        <>
          {step === "status" && renderStatus()}
          {step === "confirm-password" && renderConfirmPassword()}
          {step === "setup" && renderSetup()}
          {step === "recovery" && renderRecovery()}
          {step === "disable" && renderDisable()}
        </>
      )}
    </Modal>
  );
}

export default TwoFactorAuth;
